import MenuIcon from '@mui/icons-material/Menu';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useState } from 'react';
import { TbShare3 } from 'react-icons/tb';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useGlobalState } from '../../context/GlobalState';
import LanguageSelector from '../common/LanguageSelector';
import ShareModal from '../share/modal';
import './AppShell.scss';
import { useAppShellContext } from './AppShellContext';
import SideMenu from './SideMenu';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useCookies } from 'react-cookie';
import CookieConsent from 'react-cookie-consent';
import { useTranslation } from 'react-i18next';
import { GrPerformance } from 'react-icons/gr';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { RiAdvertisementLine, RiArrowDownSLine } from 'react-icons/ri';
import { SiGoogleanalytics } from 'react-icons/si';
import { VscTools } from 'react-icons/vsc';
import { initializeMinimalGA, sendToGoogleAnalytics } from '../../index';
import ManagementPanelHint from '../managementPanel/walkthrough/ManagementPanelHint';

const AppShell = (props: { children?: any }) => {
  const appShellContext = useAppShellContext();
  const location = useLocation();
  const globalState = useGlobalState();
  const { t } = useTranslation();
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(false);
  const [menuButtonEl, setMenuButtonEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const [helpTooltipOpen, setHelpTooltipOpen] = useState<boolean>(false);
  const [hasBeenFocused, setHasBeenFocused] = useState<boolean>(false);

  const [showModal, setShowModal] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>(window.location.href);

  const [showModalCookie, setShowModalCookie] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [cookies, setCookie] = useCookies(['consent']);
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));

  const makeShareUrl = (url: string) => {
    const urlObj = new URL(url);
    const searchParams = urlObj.searchParams;

    // Remove 'zoom' and 'pinsize' from search parameters
    searchParams.delete('zoom');
    searchParams.delete('pinsize');

    // Rebuild the URL with the filtered search parameters
    const filteredParams = searchParams.toString();
    const queryString = filteredParams ? `?${filteredParams}` : '';

    return urlObj.origin + urlObj.pathname + queryString;
  };

  const toggleSideMenu = (b?: boolean) => {
    if (b) {
      setSideMenuOpen(b);
    } else {
      setSideMenuOpen(!sideMenuOpen);
    }
  };

  useEffect(() => {
    setShareUrl(window.location.href);
  }, [location.search]);

  useEffect(() => {
    const onFocus = () => setHasBeenFocused(true);

    document.addEventListener('focus', onFocus, true);
    document.addEventListener('touchstart', onFocus, true);
    document.addEventListener('mousedown', onFocus, true);
    return () => {
      document.removeEventListener('focus', onFocus, true);
      document.removeEventListener('touchstart', onFocus, true);
      document.removeEventListener('mousedown', onFocus, true);
    };
  }, []);

  const handleShareButtonClick = () => {
    setShowModal(true);
  };

  const handleCloseModalCookie = () => {
    setShowModalCookie(false);
  };

  useEffect(() => {
    globalState.resetTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    // If embedded in iframe, always hide the banner
    if (appShellContext.isInIframe) {
      setShowBanner(false);
      return;
    }

    // Check for user consent when not in iframe
    const userConsent = cookies['consent'];
    if (userConsent === 'accepted') {
      // User previously accepted cookies
      setShowBanner(false); // Close the banner if consent is already accepted
      // initialize google analytics
      if (process.env.NODE_ENV === 'development') {
        console.log('User accepted the cookie consent and preferences already:', cookies);
      }
      if (process.env.NODE_ENV === 'production') {
        sendToGoogleAnalytics();
      }
    }
  }, [cookies, appShellContext.isInIframe]);

  const handleAccept = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('User accepted the cookie consent and preferences:', cookies);
    }

    // Update consent mode
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted'
      });
    }

    // Store in cookies with appropriate settings for iframe mode
    setCookie('consent', 'accepted', {
      path: '/',
      maxAge: 180 * 24 * 60 * 60, // 180 days
      sameSite: appShellContext.isInIframe ? 'none' : 'lax', // Use 'none' for cross-site iframes
      secure: true // Required for sameSite: 'none'
    });
    setShowModalCookie(false);
    setShowBanner(false);
  };

  const handleDecline = () => {
    console.log('User declined the cookie consent');

    // Update consent mode
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied'
      });
    }

    // Store in cookies with appropriate settings for iframe mode
    setCookie('consent', 'declined', {
      path: '/',
      maxAge: 180 * 24 * 60 * 60, // 180 days
      sameSite: appShellContext.isInIframe ? 'none' : 'lax', // Use 'none' for cross-site iframes
      secure: true // Required for sameSite: 'none'
    });
    setShowModalCookie(false);
    setShowBanner(false);
    if (process.env.NODE_ENV === 'production') {
      // Initialize restricted analytics
      initializeMinimalGA();
    }
  };

  const handleCustomize = () => {
    setShowModalCookie(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const initialSections = [
    {
      id: 1,
      title: t('cookieConsent.necessary'),
      icon: <IoAlertCircleOutline />,
      icon2: <RiArrowDownSLine />,
      defaultText: t('cookieConsent.necessaryDesc'),
      additionalText: t('cookieConsent.noCookies'),
      expanded: false
    },
    {
      id: 2,
      title: t('cookieConsent.functional'),
      icon: <VscTools />,
      icon2: <RiArrowDownSLine />,
      defaultText: t('cookieConsent.functionalDesc'),
      additionalText: t('cookieConsent.noCookies'),
      expanded: false
    },
    {
      id: 3,
      title: t('cookieConsent.analytics'),
      icon: <SiGoogleanalytics />,
      icon2: <RiArrowDownSLine />,
      defaultText: t('cookieConsent.analyticsDesc'),
      additionalText: t('cookieConsent.noCookies'),
      expanded: false
    },
    {
      id: 4,
      title: t('cookieConsent.performance'),
      icon: <GrPerformance />,
      icon2: <RiArrowDownSLine />,
      defaultText: t('cookieConsent.performanceDesc'),
      additionalText: t('cookieConsent.noCookies'),
      expanded: false
    },
    {
      id: 5,
      title: t('cookieConsent.advertisement'),
      icon: <RiAdvertisementLine />,
      icon2: <RiArrowDownSLine />,
      defaultText: t('cookieConsent.advertisementDesc'),
      additionalText: t('cookieConsent.noCookies'),
      expanded: false
    }
  ];

  const [sections, setSections] = useState(initialSections);

  // Update sections when language changes
  useEffect(() => {
    setSections(initialSections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const toggleExpand2 = (id: any) => {
    const updatedSections = sections.map((section) =>
      section.id === id ? { ...section, expanded: !section.expanded } : section
    );
    setSections(updatedSections);
  };

  /**Receives the container ID  e.g. ink-qr-container  */

  return (
    <div className='appShellContainer'>
      {!appShellContext.isInIframe && (
        <AppBar position='relative' className='appBarStyle'>
          <Toolbar>
            {appShellContext.headerLeft ?? (
              <Tooltip title={t('app.menu')}>
                <IconButton
                  ref={setMenuButtonEl}
                  onClick={() => {
                    toggleSideMenu(true);
                  }}
                  size='large'
                  edge='end'
                  color='inherit'
                  aria-label='menu'
                  sx={{ mr: 1.5, flexGrow: 0 }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t('app.home')}>
              <IconButton
                color='inherit'
                aria-label='brand'
                sx={{ flexGrow: 1 }}
                onClick={() => {
                  navigate('/', { replace: true });
                }}
              >
                <img
                  src={globalState.currentTheme.logo}
                  alt='brand'
                  style={{ maxHeight: isMobileDevice ? 'auto' : '1.5rem', maxWidth: isMobileDevice ? '150px' : 'auto' }}
                />
              </IconButton>
            </Tooltip>

            <IconButton
              onClick={() => setHelpTooltipOpen((prev) => !prev)}
              size={isMobileDevice ? 'small' : 'large'}
              edge='end'
              color='inherit'
              aria-label='menu'
              sx={!isMobileDevice ? { mr: 1.5, flexGrow: 0 } : {}}
            >
              <RateReviewIcon />
            </IconButton>
            <Dialog open={helpTooltipOpen} onClose={() => setHelpTooltipOpen(false)}>
              <DialogTitle className='info-dialog-title' sx={{ backgroundColor: '#000927' }}>
                {t('helpDialog.title')}
              </DialogTitle>
              <DialogContent className='info-dialog-content' style={{ marginTop: '10px' }}>
                <Typography display={'block'}>{t('helpDialog.description')}</Typography>
                <Typography display={'block'}>{t('helpDialog.description2')}</Typography>
                <br />
                <Typography display={'block'}>{t('helpDialog.question')}</Typography>
                <Typography display={'block'}>
                  {t('helpDialog.helpText')}{' '}
                  <a href='mailto:info@närvaror.com?subject=Rapportera%20fel!'>{t('helpDialog.sendEmail')}</a>
                </Typography>
                <br />
                <Typography display={'block'}>
                  {t('helpDialog.learnMore')} <a href='https://närvaror.com/'>Närvaror</a>
                </Typography>
              </DialogContent>
              <DialogActions className='warning-dialog-content'>
                <Button onClick={() => setHelpTooltipOpen(false)} variant='contained'>
                  {t('helpDialog.close')}
                </Button>
              </DialogActions>
            </Dialog>
            {isMobileDevice ? (
              <IconButton
                onClick={handleShareButtonClick}
                color='inherit'
                sx={{ borderRadius: '5rem' }}
                aria-label={t('app.share')}
              >
                <TbShare3 size={18} />
              </IconButton>
            ) : (
              <Button
                sx={{ bgcolor: grey[500], color: 'white', borderRadius: '5rem', textTransform: 'none' }}
                onClick={handleShareButtonClick}
                startIcon={<TbShare3 size={18} />}
                aria-label={t('app.share')}
              >
                {t('app.share')}
              </Button>
            )}

            {showModal && <ShareModal shareUrl={makeShareUrl(shareUrl)} onClose={handleCloseModal} />}

            <LanguageSelector />

            <SideMenu anchor='left' open={sideMenuOpen} toggleMenu={toggleSideMenu} />
            <ManagementPanelHint anchorEl={appShellContext.headerLeft ? null : menuButtonEl} hidden={sideMenuOpen} />
          </Toolbar>
        </AppBar>
      )}

      {showBanner && hasBeenFocused && (
        <CookieConsent
          location='bottom'
          buttonText=''
          declineButtonText=''
          cookieName='consent'
          enableDeclineButton
          onAccept={handleAccept}
          onDecline={handleDecline}
          style={{
            background: '#000a27',
            borderRadius: '10px',
            textAlign: 'center',
            color: 'white',
            border: 'none',
            opacity: 0.85
          }}
          buttonStyle={{
            display: 'none'
          }}
          declineButtonStyle={{
            display: 'none'
          }}
        >
          <div className={`cookie-banner${window.self !== window.top ? ' cookie-banner-iframe' : ''}`}>
            <span>
              <h3>{t('cookieConsent.title')}</h3>
              {t('cookieConsent.description')}
            </span>
            <div className='cookie-buttons'>
              <Button data-testid='cookie-accept' onClick={handleAccept}>
                {t('cookieConsent.acceptAll')}
              </Button>
              <Button data-testid='cookie-reject' onClick={handleDecline}>
                {t('cookieConsent.rejectAll')}
              </Button>
              <Button className='customize-button' onClick={handleCustomize}>
                {t('cookieConsent.customize')}
              </Button>
            </div>
          </div>
        </CookieConsent>
      )}
      <div className={`cookie-modal ${showModalCookie ? 'active' : ''}`} onClick={handleCloseModalCookie}>
        <div className='cookie-modal-content' onClick={(e) => e.stopPropagation()}>
          <div className='modal-header'>
            <h3>{t('cookieConsent.customizeTitle')}</h3>
            <button className='close-button' onClick={handleCloseModalCookie}>
              {t('cookieConsent.close')}
            </button>
          </div>
          <hr></hr>
          <div>
            <p className='text'>{t('cookieConsent.description1')}</p>
            <p className='text'>{t('cookieConsent.description2')}</p>
            {expanded ? (
              <>
                <p className='text'>{t('cookieConsent.description3')}</p>
                <p className='text'>{t('cookieConsent.description4')}</p>
                <button className='expand' onClick={toggleExpand}>
                  {t('cookieConsent.showLess')}
                </button>
              </>
            ) : (
              <button className='expand' onClick={toggleExpand}>
                {t('cookieConsent.showMore')}
              </button>
            )}
          </div>
          <svg height='1' width='100%'>
            <line x1='0' y1='0' x2='100%' y2='0' style={{ stroke: 'white', strokeWidth: '1' }} />
          </svg>
          <div>
            {sections.map((section, index) => (
              <div key={section.id}>
                <span onClick={() => toggleExpand2(section.id)} style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'white',
                      fontSize: '20px',
                      padding: '10px'
                    }}
                  >
                    {section.icon}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    {section.title}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'white',
                      fontSize: '20px',
                      padding: '5px'
                    }}
                  >
                    {section.icon2}
                  </div>
                </span>
                <p className='text'>{section.defaultText}</p>
                {section.expanded && <div className='extraText'>{section.additionalText}</div>}
                {index !== sections.length - 1 && ( // Render the line for all sections except the last one
                  <svg height='1' width='100%'>
                    <line x1='0' y1='0' x2='100%' y2='0' style={{ stroke: 'white', strokeWidth: '1' }} />
                  </svg>
                )}
              </div>
            ))}
          </div>
          <div className='modal-buttons'>
            <Button className='customize-button' onClick={handleDecline}>
              {t('cookieConsent.rejectAll')}
            </Button>
            <Button className='customize-button' onClick={handleAccept}>
              {t('cookieConsent.savePreferences')}
            </Button>
            <Button className='customize-button' onClick={handleAccept}>
              {t('cookieConsent.acceptAll')}
            </Button>
          </div>
        </div>
      </div>

      <div className={`main ${appShellContext.isInIframe ? 'main-iframe' : ''}`}>
        <Outlet />
      </div>
    </div>
  );
};

export default AppShell;
